import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  AutocompleteRequest,
  FullAddressDetails,
  HttpOptions,
  MatchedlocationsDetails,
  ParseDetails,
  Prediction
} from '../+state/geo-coder/geo-coder.models';
import { State } from '../+state/selectors';
import * as fromRoot from '../+state/selectors';
import { SearchContext } from '../+state/service-locator/service-locator.models';

@Injectable({
  providedIn: 'root'
})
export class GeoCoderService {
  private geoApi: string;

  constructor(
    private http: HttpClient,
    private store: Store<State>
  ) {
    this.store.select(fromRoot.getGeoApiUrl).subscribe(url => {
      this.geoApi = url;
    });
  }

  /**
   * Call Geo Api to get addresses to autocomplete based on the address
   * the user starts typing
   */
  autocomplete(
    requestObj: AutocompleteRequest,
    context: SearchContext
  ): Observable<Array<Prediction>> {
    let params: HttpParams;

    if (requestObj.location && requestObj.radius) {
      params = new HttpParams()
        .set('input', requestObj.input)
        .set('types', requestObj.types)
        .set('location', requestObj.location)
        .set('radius', requestObj.radius);
    } else {
      params = new HttpParams().set('input', requestObj.input).set('types', requestObj.types);
    }

    const options = {
      headers: this.getHeaders(),
      params: params
    };

    return this.getAutoComplete(options);
  }

  private getAutoComplete(options: HttpOptions): Observable<Array<Prediction>> {
    return this.http.get<Array<Prediction>>(this.geoApi + 'autocomplete', options).pipe(
      map((res: any) => {
        return res.predictions;
      })
    );
  }

  /**
   * Call Geo Api to get full address object based on a place id from the autocomplete
   * @param placeId place_id of the address in the autocomplete dropdown that the user selected
   */
  getDetails(placeId: string, context: SearchContext): Observable<FullAddressDetails> {
    let params: HttpParams;
    params = new HttpParams().set('placeid', placeId);
    const options = {
      headers: this.getHeaders(),
      params
    };

    return this.getDetailOptions(options);
  }

  private getDetailOptions(options: HttpOptions): Observable<FullAddressDetails> {
    return this.http.get<any>(this.geoApi + 'details', options).pipe(
      map((res: any) => {
        return res.places;
      })
    );
  }

  /**
   * Given an address string determine if it is a valid location
   * Used to validate the zip code
   * @param value a zip code the user inputs
   */
  parse(value: string, context: SearchContext): Observable<MatchedlocationsDetails> {
    let params: HttpParams;
    params = new HttpParams().set('zip5', value);
    const options = {
      headers: this.getHeaders(),
      params: params
    };
    return this.getParse(options);
  }

  private getParse(options: HttpOptions): Observable<MatchedlocationsDetails> {
    return this.http.get<any>(this.geoApi + 'matchedlocations', options);
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json'
    });
  }
}
